// WHAT: Settings component for Tuya smart home integration (placeholder/migration notice)
// WHY:  Provides information about Tuya integration migration status
// DOMAIN: Settings → Tuya
// DEPENDENCIES: UI components
// RISKS:
//   - NOTE: This is a placeholder - no actual configuration available
//   - NOTE: Migration status may be outdated
//   - NOTE: No actual configuration options - users cannot configure Tuya

import { Home, Wrench, AlertCircle, Info } from "lucide-react";

import { Card, Stack, Heading, Badge } from "@/components/ui";

/**
 * WHAT:
 *   Settings component that displays information about Tuya smart home
 *   integration migration status and current implementation.
 *
 * WHY:
 *   Informs users about the ongoing migration from Tuya Cloud to TinyTuya
 *   and provides status information.
 *
 * BEHAVIOR:
 *   - Displays migration notice
 *   - Shows current implementation status
 *   - Describes planned implementation
 *   - Placeholder for future configuration
 *
 * RISKS:
 *   - NOTE: This is a placeholder - no actual configuration available
 *   - NOTE: Migration status may be outdated
 *   - NOTE: No actual configuration options - users cannot configure Tuya
 *
 * @returns {JSX.Element} Tuya settings component
 *
 */
// Reason: Component is used via routing in src/routes/index.tsx, not directly imported

export function TuyaSettings() {
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="flex items-center justify-center w-12 h-12 rounded-tracq bg-gradient-to-br from-tracq-deep-blue to-tracq-bright-blue shadow-tracq-card">
            <Home className="w-6 h-6 text-white" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-white" level="h2" size="lg">
              Tuya Smart Home Integration
            </Heading>
            <p className="text-slate-400 text-sm">
              Configure your Tuya smart home devices and automation.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Migration Notice */}
      <Card className="bg-yellow-500/10 border-yellow-500/20" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={3}>
          <AlertCircle className="w-6 h-6 text-yellow-400" />
          <Stack direction="col" gap={1}>
            <Heading className="text-yellow-300" level="h3" size="md">
              Migration in Progress
            </Heading>
            <p className="text-yellow-200 text-sm">
              Tuya integration is being migrated from the official Tuya Cloud to TinyTuya for better
              performance and reliability.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Current Status */}
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          <Stack align="center" direction="row" gap={3}>
            <Wrench className="w-5 h-5 text-blue-400" />
            <Heading className="text-white" level="h3" size="md">
              Integration Status
            </Heading>
            <Badge size="sm" variant="warning">
              Under Development
            </Badge>
          </Stack>

          <div className="space-y-3">
            <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600/50">
              <Stack align="center" className="mb-2" direction="row" gap={2}>
                <Info className="w-4 h-4 text-blue-400" />
                <span className="font-medium text-white">Current Implementation</span>
              </Stack>
              <p className="text-slate-300 text-sm">
                Currently using the official Tuya Cloud API with Access ID and Access Key
                authentication.
              </p>
            </div>

            <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600/50">
              <Stack align="center" className="mb-2" direction="row" gap={2}>
                <Info className="w-4 h-4 text-green-400" />
                <span className="font-medium text-white">Planned Implementation</span>
              </Stack>
              <p className="text-slate-300 text-sm">
                Migrating to TinyTuya for local device control with improved performance and reduced
                cloud dependency. The required credentials and configuration will be updated once
                the migration is complete.
              </p>
            </div>
          </div>
        </Stack>
      </Card>

      {/* Placeholder for Future Configuration */}
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          <Heading className="text-white" level="h3" size="md">
            Configuration
          </Heading>
          <div className="p-6 bg-slate-700/30 rounded-lg border-2 border-dashed border-slate-600/50">
            <Stack align="center" direction="col" gap={3}>
              <Wrench className="w-8 h-8 text-slate-500" />
              <Stack align="center" direction="col" gap={1}>
                <p className="text-slate-400 font-medium">Configuration Coming Soon</p>
                <p className="text-slate-500 text-sm text-center">
                  TinyTuya configuration options will be available here once the migration is
                  complete.
                </p>
              </Stack>
            </Stack>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
